import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts";
import { IconButton } from "@mui/material";
import { ChevronRight, AlertTriangle } from "lucide-react";

import { ChartTooltip } from "./chartTooltip";
import React from "react";
import { useTranslate, useGo } from "@refinedev/core";

import backgroundImage from "../../assets/svg/person-alert.svg";
import dayjs from "dayjs";
import axiosInstance from "../../api/axios-config";

export const DailyAlerts: React.FC = () => {
  const t = useTranslate();
  const go = useGo();
  const [dailyAlertsData, setDailyAlertsData] = React.useState<any[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  // Calculate total alerts across all days
  const totalAlerts = React.useMemo(() => {
    console.log(t("dashboard.alerts.calculating"));
    return dailyAlertsData.reduce((sum, item) => sum + (item.value || 0), 0);
  }, [dailyAlertsData]);

  const getDailyAlerts = async () => {
    console.log(t("dashboard.alerts.fetching.start"));
    const start = dayjs()
      .subtract(30, "days")
      .startOf("day")
      .format("YYYY-MM-DD");

    try {
      console.log(t("dashboard.alerts.fetching.url", { url: `/notifications/stats/daily-alert?startDate=${start}` }));
      const { data: results } = await axiosInstance.get(
        `/notifications/stats/daily-alert?startDate=${start}`
      );

      console.log(t("dashboard.alerts.fetching.received"), results);
      const hasData = Object.values(results).some((value: any) => value > 0);

      if (hasData) {
        console.log(t("dashboard.alerts.processing.data"));
        const formattedData = Object.keys(results).map((key) => {
          return {
            date: dayjs(key).format('DD/MM/YYYY'),
            title: t("dashboard.alerts.title"),
            value: results[key] as number,
          };
        });
        console.log(t("dashboard.alerts.processing.formatted"), formattedData);
        setDailyAlertsData(formattedData);
        setIsLoading(false);
      } else {
        console.log(t("dashboard.alerts.noData"));
        await getAlertHistoryAndCalculateStats(start);
      }
    } catch (error) {
      console.error(t("dashboard.alerts.error.fetch"), error);
      const start = dayjs().subtract(30, "days").startOf("day").format("YYYY-MM-DD");
      await getAlertHistoryAndCalculateStats(start);
    }
  };

  const getAlertHistoryAndCalculateStats = async (startDate: string) => {
    console.log(t("dashboard.alerts.history.start"), startDate);
    try {
      console.log(t("dashboard.alerts.history.fetching"), `/alert-history?startDate=${startDate}`);
      const { data: alertResults } = await axiosInstance.get(
        `/alert-history?startDate=${startDate}`
      );

      console.log(t("dashboard.alerts.history.received"), alertResults);
      const dailyCounts: Record<string, number> = {};

      const startDay = dayjs(startDate);
      const today = dayjs();

      for (let d = startDay; d.isBefore(today) || d.isSame(today, 'day'); d = d.add(1, 'day')) {
        const dateKey = d.format('YYYY-MM-DD');
        dailyCounts[dateKey] = 0;
      }

      console.log(t("dashboard.alerts.history.initial"), dailyCounts);

      alertResults.forEach((alert: any) => {
        if (alert.createdAt) {
          const dateKey = dayjs(alert.createdAt).format('YYYY-MM-DD');
          if (dailyCounts[dateKey] !== undefined) {
            dailyCounts[dateKey]++;
          }
        }
      });

      console.log(t("dashboard.alerts.history.processed"), dailyCounts);

      const formattedData = Object.keys(dailyCounts).map((key) => {
        return {
          date: dayjs(key).format('DD/MM/YYYY'),
          title: t("dashboard.alerts.title"),
          value: dailyCounts[key],
        };
      });

      console.log(t("dashboard.alerts.history.final"), formattedData);
      setDailyAlertsData(formattedData);
      setIsLoading(false);

    } catch (error) {
      console.error(t("dashboard.alerts.error.history"), error);
      setDailyAlertsData([]);
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    console.log(t("dashboard.alerts.mounted"));
    getDailyAlerts();
  }, []);

  return (
    <Box
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, #667eea 0%, #764ba2 100%)`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right",
        backgroundSize: "contain",
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          background: `url(${backgroundImage})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right center",
          backgroundSize: "contain",
          opacity: 0.3,
          zIndex: 1
        }
      }}
    >
      {/* Navigation Button */}
      <IconButton
        onClick={() => {
          console.log(t("dashboard.alerts.navigation"));
          go({ to: "/alert-history" });
        }}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          width: 32,
          height: 32,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <ChevronRight size={16} />
      </IconButton>

      <Stack sx={{ position: 'relative', zIndex: 2, p: 3, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          {/* Icon */}
          <AlertTriangle size={40} color="rgba(255, 255, 255, 0.9)" />
          {!isLoading && dailyAlertsData.length > 0 ? (
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mb: 0.5,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {totalAlerts}
          </Typography>
          ) : (
            <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontWeight: 700,
              mb: 0.5,
              fontFamily: 'Inter, sans-serif'
            }}
          >
            --
          </Typography>
          )}
        </Stack>
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255, 255, 255, 0.9)",
            fontWeight: 500,
            mb: 1,
            fontFamily: 'Inter, sans-serif'
          }}
        >
          {t("dashboard.dailyAlerts")}
        </Typography>
        {isLoading && (
          <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 14 }}>
            {t("dashboard.alerts.loading")}
          </Typography>
        )}
        {!isLoading && dailyAlertsData.length === 0 && (
          <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 14 }}>
            {t("dashboard.alerts.noDataAvailable")}
          </Typography>
        )}
        {!isLoading && dailyAlertsData.length > 0 && (
          <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: 14 }}>
            {t("dashboard.alerts.lastUpdated")
              .replace("{{date}}", dayjs().format("DD/MM/YYYY"))
              .replace("{{time}}", dayjs().format("HH:mm"))}
          </Typography>
        )}
      </Stack>
      <Box sx={{ flex: 1, mb: 2, px:2, position: 'relative', zIndex: 2 }}>
        <ResponsiveContainer width="100%">
          <LineChart data={dailyAlertsData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke="rgba(255, 255, 255, 0.8)"
              strokeWidth={3}
              dot={{
                fill: 'rgba(255, 255, 255, 0.9)',
                strokeWidth: 2,
                r: 4
              }}
              activeDot={{
                r: 6,
                fill: '#fff',
                stroke: 'rgba(255, 255, 255, 0.8)',
                strokeWidth: 2
              }}
            />
            <Tooltip content={<ChartTooltip />} />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
